<template>
  <b-modal v-model="modal" title="Importar Itens via Planilha" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true" hide-footer>
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>

    <!-- Step 1: Instructions -->
    <div v-if="step === 1">
      <!-- Header visual -->
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-spreadsheet"></i>
        </div>
        <h5 class="mb-1">Importação em massa de itens</h5>
        <p class="text-muted mb-0">Adicione ou atualize centenas de itens de uma só vez usando uma planilha Excel ou CSV.</p>
      </div>

      <!-- How it works -->
      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-info-circle text-primary mr-1"></i> Como funciona</h6>
          <div class="steps-list">
            <div class="step-item">
              <span class="step-number">1</span>
              <div>
                <strong>Baixe o template</strong>
                <p class="text-muted mb-0 font-size-12">O template já vem com as colunas configuradas para este controle.</p>
              </div>
            </div>
            <div class="step-item">
              <span class="step-number">2</span>
              <div>
                <strong>Preencha os dados</strong>
                <p class="text-muted mb-0 font-size-12">Uma linha por item. Respeite os campos obrigatórios.</p>
              </div>
            </div>
            <div class="step-item">
              <span class="step-number">3</span>
              <div>
                <strong>Faça o upload</strong>
                <p class="text-muted mb-0 font-size-12">Itens com identificador já existente serão <strong>atualizados</strong>, novos serão criados.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fields preview -->
      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-columns text-primary mr-1"></i> Colunas da planilha</h6>
          <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0 font-size-13">
              <thead class="thead-light">
                <tr>
                  <th style="width: 35%">Coluna</th>
                  <th style="width: 15%">Tipo</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>identificador</code> <span class="badge badge-soft-danger font-size-10 ml-1">obrigatório</span></td>
                  <td><span class="text-muted">texto</span></td>
                  <td>Refere-se ao campo <strong>"{{ controlData.identifier_label || 'Identificador' }}"</strong> do item. Deve ser único — se já existir um item com o mesmo valor, ele será atualizado.</td>
                </tr>
                <tr>
                  <td><code>nome</code> <span class="badge badge-soft-danger font-size-10 ml-1">obrigatório</span></td>
                  <td><span class="text-muted">texto</span></td>
                  <td>Nome de exibição do item.</td>
                </tr>
                <tr>
                  <td><code>limite</code></td>
                  <td><span class="text-muted">número</span></td>
                  <td>Máximo de inscrições permitidas. Deixe vazio para ilimitado.</td>
                </tr>
                <tr v-if="['per_group', 'per_item_and_group'].includes(controlData.vacancy_mode)">
                  <td><code>grupo</code></td>
                  <td><span class="text-muted">texto</span></td>
                  <td>
                    Nome do grupo ao qual o item pertence.
                    <span v-if="groupsList.length > 0" class="d-block mt-1">
                      <small class="text-muted">Grupos disponíveis:
                        <span v-for="(g, i) in groupsList" :key="g.id">
                          <strong>{{ g.name }}</strong><span v-if="i < groupsList.length - 1">, </span>
                        </span>
                      </small>
                    </span>
                  </td>
                </tr>
                <tr v-if="controlData.assign_participant_group">
                  <td><code>grupo_do_participante</code></td>
                  <td><span class="text-muted">texto</span></td>
                  <td>
                    Nome do grupo ao qual o participante será atribuído após a inscrição.
                    <span v-if="participantGroupsList.length > 0" class="d-block mt-1">
                      <small class="text-muted">Grupos disponíveis:
                        <span v-for="(g, i) in participantGroupsList" :key="g.id">
                          <strong>{{ g.name }}</strong><span v-if="i < participantGroupsList.length - 1">, </span>
                        </span>
                      </small>
                    </span>
                  </td>
                </tr>
                <tr v-for="field in (controlData.fields_schema || [])" :key="field.name">
                  <td><code>{{ field.label }}</code> <span class="badge badge-soft-info font-size-10 ml-1">customizado</span></td>
                  <td><span class="text-muted">{{ field.type === 'number' ? 'número' : 'texto' }}</span></td>
                  <td>Campo personalizado configurado neste controle.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Download template button -->
      <div class="text-center mb-3">
        <button type="button" class="btn btn-outline-success" @click="downloadTemplate()">
          <i class="bx bx-download mr-1"></i> Baixar Template (.xlsx)
        </button>
      </div>

      <hr class="mb-3 mt-1" />

      <!-- Footer actions -->
      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="close()">Cancelar</b-button>
        <b-button variant="primary" @click="step = 2">
          Já tenho a planilha pronta <i class="bx bx-right-arrow-alt ml-1"></i>
        </b-button>
      </div>
    </div>

    <!-- Step 2: Upload -->
    <div v-if="step === 2">
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-cloud-upload"></i>
        </div>
        <h5 class="mb-1">Enviar planilha</h5>
        <p class="text-muted mb-0">Selecione o arquivo preenchido para iniciar a importação.</p>
      </div>

      <!-- Drop zone -->
      <div
        class="drop-zone mb-3"
        :class="{ 'drop-zone-active': isDragging, 'drop-zone-has-file': file }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="d-none"
          @change="handleFileSelect"
        />
        <div v-if="!file" class="text-center">
          <i class="bx bx-cloud-upload drop-zone-icon text-muted"></i>
          <p class="mb-1">Arraste o arquivo aqui ou <strong class="text-primary">clique para selecionar</strong></p>
          <small class="text-muted">Formatos aceitos: .xlsx, .xls, .csv</small>
        </div>
        <div v-else class="text-center">
          <i class="bx bx-file drop-zone-icon text-success"></i>
          <p class="mb-1 font-weight-medium">{{ file.name }}</p>
          <small class="text-muted">{{ formatFileSize(file.size) }}</small>
          <br />
          <a href="#" @click.stop.prevent="file = null" class="text-danger font-size-12 mt-1 d-inline-block">
            <i class="bx bx-x"></i> Remover arquivo
          </a>
        </div>
      </div>

      <!-- Info alert -->
      <div class="alert alert-info font-size-13 mb-3 d-flex align-items-start" role="alert">
        <i class="bx bx-bulb mr-2 font-size-16 mt-0"></i>
        <div>
          <strong>Dica:</strong> Itens com o mesmo <strong>identificador</strong> serão atualizados com os novos dados.
          Novos identificadores criarão itens novos. Todos entram como <strong>Ativo</strong>.
        </div>
      </div>

      <hr class="mb-3 mt-1" />

      <!-- Footer actions -->
      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="step = 1">
          <i class="bx bx-left-arrow-alt mr-1"></i> Voltar
        </b-button>
        <b-button variant="primary" :disabled="!file || loading" @click="applyImport()">
          <i class="bx bx-upload mr-1"></i> Importar Itens
        </b-button>
      </div>
    </div>

    <!-- Step 3: Processing + Result -->
    <div v-if="step === 3">
      <!-- Phase A: Processing (progress bar) -->
      <div v-if="processing" class="text-center py-4">
        <div class="import-icon-wrapper mb-3">
          <i class="bx bx-loader-alt bx-spin"></i>
        </div>
        <h5 class="mb-2">Processando planilha...</h5>
        <p class="text-muted mb-3">Aguarde enquanto os itens são importados.</p>
        <div class="progress mx-auto" style="max-width: 400px; height: 8px;">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
            role="progressbar"
            :style="{ width: progressPercent + '%' }"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <p class="text-muted font-size-13 mt-2 mb-0">{{ progressPercent }}%</p>
      </div>

      <!-- Phase B: Results -->
      <div v-else>
        <div class="text-center">
          <div class="import-icon-wrapper mb-3" :class="{ 'import-icon-success': !hasErrors, 'import-icon-warning': hasErrors }">
            <i :class="hasErrors ? 'bx bx-error' : 'bx bx-check-circle'"></i>
          </div>
          <h5 class="mb-2">Importação concluída!</h5>
        </div>

        <div class="row justify-content-center mt-3 mb-3">
          <div class="col-auto">
            <div class="result-card result-card-success">
              <span class="result-number">{{ importResult.imported }}</span>
              <span class="result-label">importados</span>
            </div>
          </div>
          <div class="col-auto" v-if="importResult.errors > 0">
            <div class="result-card result-card-danger">
              <span class="result-number">{{ importResult.errors }}</span>
              <span class="result-label">erros</span>
            </div>
          </div>
        </div>

        <p class="text-muted text-center" v-if="!hasErrors">Todos os itens foram processados com sucesso.</p>

        <!-- Error list -->
        <div v-if="errorLogEntries.length > 0" class="mt-3">
          <h6 class="mb-2"><i class="bx bx-x-circle text-danger mr-1"></i> Itens com erro</h6>
          <div class="border rounded" style="max-height: 250px; overflow-y: auto; background: #f8f9fa;">
            <div
              v-for="(entry, i) in errorLogEntries"
              :key="i"
              class="d-flex align-items-start py-2 px-3"
              :class="{ 'border-bottom': i < errorLogEntries.length - 1 }"
            >
              <i class="bx bx-x-circle text-danger mr-2 mt-1 font-size-14"></i>
              <div class="font-size-13">
                <strong v-if="entry.identifier">{{ entry.identifier }}</strong>
                <span v-if="entry.identifier && entry.name"> — </span>
                <span v-if="entry.name">{{ entry.name }}</span>
                <span v-if="entry.message" class="d-block text-danger font-size-12 mt-1">{{ entry.message }}</span>
              </div>
            </div>
          </div>
        </div>

        <hr class="mb-3 mt-3" />

        <div class="text-center">
          <b-button variant="primary" @click="finish()">Fechar</b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { errorMessage } from '@/components/alertsMessages'
import { importItemsFile, downloadImportTemplate } from './services'

export default {
  name: 'ImportItems',
  components: {
    Loading
  },
  props: {
    controlId: {
      type: [String, Number],
      required: true
    },
    controlData: {
      type: Object,
      default: () => ({})
    },
    groupsList: {
      type: Array,
      default: () => []
    },
    participantGroupsList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      file: null,
      modal: false,
      loading: false,
      isDragging: false,
      step: 1,
      processing: false,
      progressPercent: 0,
      progressTimer: null,
      importResult: {
        imported: 0,
        errors: 0,
        log: []
      }
    }
  },
  computed: {
    hasErrors () {
      return this.importResult.errors > 0
    },
    errorLogEntries () {
      return this.importResult.log.filter(e => e.status === 'error')
    }
  },
  methods: {
    open () {
      this.file = null
      this.loading = false
      this.isDragging = false
      this.step = 1
      this.processing = false
      this.progressPercent = 0
      this.importResult = { imported: 0, errors: 0, log: [] }
      this.modal = true
    },
    handleFileSelect (e) {
      const files = e.target.files
      if (files && files.length > 0) {
        this.file = files[0]
      }
    },
    handleDrop (e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        const file = files[0]
        const ext = file.name.split('.').pop().toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext)) {
          this.file = file
        } else {
          errorMessage('Alerta: Formato não suportado. Use .xlsx, .xls ou .csv')
        }
      }
    },
    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    async applyImport () {
      if (!this.file) return
      this.processing = true
      this.progressPercent = 0
      this.step = 3
      this.startProgress()
      try {
        const formData = new FormData()
        formData.append('file', this.file)
        const res = await importItemsFile(this.controlId, formData)
        this.importResult = {
          imported: res.data.imported || 0,
          errors: res.data.errors || 0,
          log: res.data.log || []
        }
        this.stopProgress()
        this.progressPercent = 100
        await new Promise(resolve => setTimeout(resolve, 300))
        this.processing = false
        this.$emit('imported')
      } catch (err) {
        this.stopProgress()
        this.processing = false
        const msg = err.response && err.response.data ? err.response.data.message : err.message
        errorMessage('Alerta: ' + msg)
        this.step = 2
      }
    },
    startProgress () {
      this.progressTimer = setInterval(() => {
        if (this.progressPercent < 90) {
          const remaining = 90 - this.progressPercent
          const increment = Math.max(0.5, remaining * 0.08)
          this.progressPercent = Math.min(90, Math.round((this.progressPercent + increment) * 10) / 10)
        }
      }, 200)
    },
    stopProgress () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    async downloadTemplate () {
      try {
        const res = await downloadImportTemplate(this.controlId)
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'template_importacao_itens.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        errorMessage('Alerta: Falha ao baixar template.')
      }
    },
    finish () {
      this.modal = false
    },
    close () {
      this.modal = false
      this.file = null
      this.step = 1
    }
  },
  watch: {
    modal (val) {
      if (!val) {
        this.file = null
        this.step = 1
        this.stopProgress()
      }
    }
  },
  beforeDestroy () {
    this.stopProgress()
  }
}
</script>

<style scoped>
.import-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(85, 110, 230, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.import-icon-wrapper i {
  font-size: 28px;
  color: #556ee6;
}
.import-icon-success {
  background-color: rgba(52, 195, 143, 0.1) !important;
}
.import-icon-success i {
  color: #34c38f !important;
}
.import-icon-warning {
  background-color: rgba(241, 180, 76, 0.1) !important;
}
.import-icon-warning i {
  color: #f1b44c !important;
}

/* Steps */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.step-number {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 50%;
  background-color: #556ee6;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

/* Drop zone */
.drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}
.drop-zone:hover {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.03);
}
.drop-zone-active {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.06);
}
.drop-zone-has-file {
  border-color: #34c38f;
  border-style: solid;
  background: rgba(52, 195, 143, 0.03);
}
.drop-zone-icon {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

/* Result cards */
.result-card {
  text-align: center;
  padding: 16px 28px;
  border-radius: 8px;
  min-width: 120px;
}
.result-card-success {
  background: rgba(52, 195, 143, 0.1);
}
.result-card-danger {
  background: rgba(244, 106, 106, 0.1);
}
.result-number {
  display: block;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}
.result-card-success .result-number {
  color: #34c38f;
}
.result-card-danger .result-number {
  color: #f46a6a;
}
.result-label {
  display: block;
  font-size: 12px;
  color: #74788d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

/* Table */
.table-sm td, .table-sm th {
  padding: 0.4rem 0.6rem;
}
code {
  font-size: 12px;
}

.vld-overlay.is-full-page {
  z-index: 1051 !important;
}
</style>
